import { useRef, useCallback, useEffect, useState } from 'react';
import { createJWT } from '../utils/jwt';
import { setupUnifiedParentResize, ParentResizeManager } from '../utils/unified-resize';
import type { SmarterElementConstraints } from '../components/SmarterElement';

export interface UseSmarterElementsOptions {
  /** Environment for element URLs */
  environment?: 'development' | 'production';
  
  /** Custom base URL for elements */
  baseUrl?: string;
  
  /** Default constraints for all elements */
  defaultConstraints?: SmarterElementConstraints;
}

export interface CreateElementOptions {
  /** Configuration data to pass to the element */
  config?: Record<string, any>;
  
  /** Size constraints for the iframe */
  constraints?: SmarterElementConstraints;
  
  /** Whether to auto-resize to content */
  autoResize?: boolean;
  
  /** Event callbacks */
  onReady?: () => void;
  onResize?: (dimensions: { height: number; width: number; elementId: string }) => void;
  onError?: (error: Error) => void;
  onEvent?: (eventType: string, data: any) => void;
}

export interface SmarterElementInstance {
  /** Unique element ID */
  elementId: string;
  
  /** Element type */
  type: string;
  
  /** Current configuration */
  config: Record<string, any>;
  
  /** Current constraints */
  constraints: SmarterElementConstraints;
  
  /** Current dimensions */
  dimensions: { height: number; width: number } | null;
  
  /** Whether element is ready */
  isReady: boolean;
  
  /** Mount element to a DOM container */
  mount: (container: string | HTMLElement) => void;
  
  /** Unmount element from DOM */
  unmount: () => void;
  
  /** Update element configuration */
  updateConfig: (newConfig: Record<string, any>) => void;
  
  /** Update size constraints */
  updateConstraints: (newConstraints: SmarterElementConstraints) => void;
  
  /** Manually trigger size recalculation */
  recalculateSize: () => void;
  
  /** Send custom message to element */
  sendMessage: (type: string, data?: any) => void;
  
  /** Open element in modal */
  openModal: (modalOptions?: ModalOptions) => Promise<void>;
  
  /** Close modal if open */
  closeModal: () => void;
  
  /** Destroy element and clean up */
  destroy: () => void;
}

export interface ModalOptions {
  /** Modal width */
  width?: string | number;
  
  /** Modal height */
  height?: string | number;
  
  /** Maximum width */
  maxWidth?: string | number;
  
  /** Maximum height */
  maxHeight?: string | number;
  
  /** Whether to show backdrop */
  backdrop?: boolean;
  
  /** Whether clicking document/backdrop dismisses modal (defaults to false) */
  dismissOnDocumentClick?: boolean;
  
  /** Whether ESC key closes modal */
  escapeClose?: boolean;
  
  /** Z-index for modal */
  zIndex?: number;
  
  /** Padding inside modal content (defaults to '10px') */
  padding?: string;
}

/**
 * useSmarterElements Hook
 * 
 * A React hook for programmatic management of SmarterElements.
 * Provides a factory-like API similar to Stripe Elements.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const elements = useSmarterElements({ environment: 'production' });
 *   
 *   const handleCreateExam = useCallback(() => {
 *     const examElement = elements.create('exam-card', {
 *       config: { examId: '123' },
 *       constraints: { minHeight: 200, maxHeight: 600 },
 *       onReady: () => console.log('Exam ready!'),
 *       onResize: (dims) => console.log('Resized:', dims)
 *     });
 *     
 *     examElement.mount('#exam-container');
 *   }, [elements]);
 *   
 *   return <button onClick={handleCreateExam}>Create Exam</button>;
 * }
 * ```
 */
export function useSmarterElements(options: UseSmarterElementsOptions = {}) {
  const {
    environment = 'development',
    baseUrl,
    defaultConstraints = {}
  } = options;

  const elementsRef = useRef<Map<string, SmarterElementInstance>>(new Map());
  const [, forceUpdate] = useState({});

  // Force re-render when elements change
  const triggerUpdate = useCallback(() => {
    forceUpdate({});
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      elementsRef.current.forEach(element => {
        element.destroy();
      });
      elementsRef.current.clear();
    };
  }, []);

  // Create element instance
  const create = useCallback((
    type: string,
    options: CreateElementOptions = {}
  ): SmarterElementInstance => {
    const elementId = `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const {
      config = {},
      constraints = {},
      autoResize = false,
      onReady,
      onResize,
      onError,
      onEvent
    } = options;

    const mergedConstraints = { ...defaultConstraints, ...constraints };
    
    let iframe: HTMLIFrameElement | null = null;
    let container: HTMLElement | null = null;
    let wrapper: HTMLElement | null = null;
    let cleanupResize: (() => void) | null = null;
    let resizeManager: ParentResizeManager | null = null;
    

    const instance: SmarterElementInstance = {
      elementId,
      type,
      config: { ...config },
      constraints: { ...mergedConstraints },
      dimensions: null,
      isReady: false,

      mount: (containerSelector: string | HTMLElement) => {
        // Get container element
        if (typeof containerSelector === 'string') {
          container = document.querySelector(containerSelector);
          if (!container) {
            throw new Error(`Container not found: ${containerSelector}`);
          }
        } else {
          container = containerSelector;
        }

        // Create wrapper div for padding
        const elementPadding = config['padding'] || '10px';
        wrapper = document.createElement('div');
        wrapper.style.padding = elementPadding;
        wrapper.style.boxSizing = 'border-box';
        wrapper.style.borderRadius = '4px';
        wrapper.style.overflow = 'hidden';

        // Create iframe
        iframe = document.createElement('iframe');
        iframe.src = getIframeUrl();
        iframe.style.border = 'none';
        iframe.style.transition = 'height 0.15s ease-out, width 0.15s ease-out';
        iframe.style.width = '100%';
        iframe.style.display = 'block';
        iframe.title = `SmarterElement: ${type}`;
        iframe.allow = 'camera; microphone; fullscreen';
        iframe.sandbox.add('allow-scripts', 'allow-same-origin', 'allow-forms', 'allow-popups', 'allow-modals');

        // Apply constraints to wrapper instead of iframe
        applyConstraints();

        // Set up unified resize handling (for DOM elements only)
        setupResizeHandling();
        
        // Set up message handling for non-resize events (for DOM elements only)
        setupEventHandling();

        // Mount wrapper to container, iframe to wrapper
        wrapper.appendChild(iframe);
        container.appendChild(wrapper);
        
        triggerUpdate();
      },

      unmount: () => {
        if (cleanupResize) {
          cleanupResize();
          cleanupResize = null;
        }
        if (wrapper && container) {
          container.removeChild(wrapper);
        }
        iframe = null;
        wrapper = null;
        container = null;
        triggerUpdate();
      },

      updateConfig: (newConfig: Record<string, any>) => {
        instance.config = { ...instance.config, ...newConfig };
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type: 'update-config',
            config: instance.config,
            elementId
          }, '*');
        }
        triggerUpdate();
      },

      updateConstraints: (newConstraints: SmarterElementConstraints) => {
        instance.constraints = { ...instance.constraints, ...newConstraints };
        applyConstraints();
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type: 'update-constraints',
            constraints: instance.constraints,
            elementId
          }, '*');
        }
        triggerUpdate();
      },

      recalculateSize: () => {
        if (resizeManager) {
          resizeManager.recalculateSize();
        } else {
          console.warn('Cannot recalculate size: resize manager not available. Element may not be mounted.');
        }
      },

      sendMessage: (type: string, data?: any) => {
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type,
            ...data,
            elementId
          }, '*');
        }
      },

      openModal: async (modalOptions: ModalOptions = {}) => {
        // Disable body scrolling
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        (instance as any).originalOverflow = originalOverflow;

        // Create modal container
        const modalContainer = document.createElement('div');
        modalContainer.id = `modal-container-${elementId}`;
        modalContainer.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: ${modalOptions.zIndex || 9999};
          font-family: system-ui, -apple-system, sans-serif;
        `;

        // Create modal content wrapper
        const modalContent = document.createElement('div');
        const modalPadding = modalOptions.padding || '10px';
        modalContent.style.cssText = `
          position: relative;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          width: ${modalOptions.width || '90%'};
          ${modalOptions.height ? `height: ${modalOptions.height};` : 'height: auto;'}
          max-width: ${modalOptions.maxWidth || '800px'};
          max-height: ${modalOptions.maxHeight || '90vh'};
          overflow: ${modalOptions.height ? 'auto' : 'hidden'};
          animation: modalSlideIn 0.3s ease-out;
          padding: ${modalPadding};
          box-sizing: border-box;
        `;

        // Add modal animations
        if (!document.getElementById('modal-animations')) {
          const style = document.createElement('style');
          style.id = 'modal-animations';
          style.textContent = `
            @keyframes modalSlideIn {
              from { opacity: 0; transform: scale(0.95) translateY(-10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `;
          document.head.appendChild(style);
        }

        // Handle backdrop click
        if (modalOptions.dismissOnDocumentClick !== false) {
          modalContainer.addEventListener('click', (e) => {
            if (e.target === modalContainer) {
              instance.closeModal();
            }
          });
        }

        // Handle escape key
        if (modalOptions.escapeClose !== false) {
          const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
              instance.closeModal();
              document.removeEventListener('keydown', handleEscape);
            }
          };
          document.addEventListener('keydown', handleEscape);
          (instance as any).escapeHandler = handleEscape;
        }

        // Create iframe with modal parameters
        const modalIframe = document.createElement('iframe');
        modalIframe.style.cssText = `
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 12px;
        `;

        // Add modal parameters to the element URL
        const modalParams = {
          modal: 'true',
          modalWidth: modalOptions.width,
          modalHeight: modalOptions.height,
          modalMaxWidth: modalOptions.maxWidth,
          modalMaxHeight: modalOptions.maxHeight,
          modalDismissOnDocumentClick: modalOptions.dismissOnDocumentClick,
          modalEscapeClose: modalOptions.escapeClose,
          modalZIndex: modalOptions.zIndex
        };

        // Get the element URL and add modal params
        const elementUrl = getIframeUrl();
        const url = new URL(elementUrl, window.location.origin);
        Object.entries(modalParams).forEach(([key, value]) => {
          if (value !== undefined) {
            url.searchParams.set(key, String(value));
          }
        });

        modalIframe.src = url.toString();

        // Set up message handling for modal iframe (including resize)
        const handleModalMessages = (event: MessageEvent) => {
          if (event.source !== modalIframe.contentWindow) return;
          
          try {
            const { type: messageType, ...data } = event.data;
            if (data.elementId !== elementId) return;

            switch (messageType) {
              case 'resize':
                // Update iframe size
                const newHeight = data.height || 400;
                modalIframe.style.height = `${newHeight}px`;
                
                // Update modal content size (respecting constraints)
                const maxWidth = modalOptions.maxWidth ? 
                  (typeof modalOptions.maxWidth === 'number' ? modalOptions.maxWidth : parseInt(modalOptions.maxWidth)) : 
                  800;
                const maxHeight = modalOptions.maxHeight ? 
                  (typeof modalOptions.maxHeight === 'number' ? modalOptions.maxHeight : 
                    modalOptions.maxHeight.includes('vh') ? 
                      (parseInt(modalOptions.maxHeight) / 100) * window.innerHeight :
                      parseInt(modalOptions.maxHeight)) : 
                  window.innerHeight * 0.9;
                
                // Apply width constraints
                const newWidth = data.width || modalContent.offsetWidth;
                const constrainedWidth = Math.min(newWidth, maxWidth);
                modalContent.style.width = modalOptions.width ? 
                  (typeof modalOptions.width === 'number' ? `${modalOptions.width}px` : modalOptions.width) : 
                  `${constrainedWidth}px`;
                
                // Apply height constraints - set explicit height to match iframe + padding
                // Parse padding value to add to iframe height (since we use box-sizing: border-box)
                const paddingValue = parseInt(modalPadding) * 2; // top + bottom padding
                const totalContentHeight = newHeight + paddingValue;
                const constrainedHeight = Math.min(totalContentHeight, maxHeight);
                modalContent.style.height = `${constrainedHeight}px`;
                modalContent.style.maxHeight = `${maxHeight}px`;
                
                // Update instance dimensions
                instance.dimensions = { height: newHeight, width: newWidth };
                onResize?.({ height: newHeight, width: newWidth, elementId });
                break;

              case 'ready':
                onReady?.();
                break;

              case 'error':
                onError?.(new Error(data.message || 'Element error'));
                break;

              default:
                onEvent?.(messageType, data);
                break;
            }
          } catch (error) {
            console.error('Error handling modal message:', error);
          }
        };

        window.addEventListener('message', handleModalMessages);

        // Assemble modal
        modalContent.appendChild(modalIframe);
        modalContainer.appendChild(modalContent);
        document.body.appendChild(modalContainer);

        // Store references for cleanup
        (instance as any).modalContainer = modalContainer;
        (instance as any).modalIframe = modalIframe;
        (instance as any).modalMessageHandler = handleModalMessages;
      },

      closeModal: () => {
        const modalContainer = (instance as any).modalContainer;
        if (modalContainer && modalContainer.parentNode) {
          // Restore body scrolling
          const originalOverflow = (instance as any).originalOverflow;
          if (originalOverflow !== undefined) {
            document.body.style.overflow = originalOverflow;
            (instance as any).originalOverflow = undefined;
          }

          // Remove escape key handler
          if ((instance as any).escapeHandler) {
            document.removeEventListener('keydown', (instance as any).escapeHandler);
            (instance as any).escapeHandler = null;
          }

          // Remove modal message handler
          if ((instance as any).modalMessageHandler) {
            window.removeEventListener('message', (instance as any).modalMessageHandler);
            (instance as any).modalMessageHandler = null;
          }

          // Add fade out animation
          modalContainer.style.opacity = '0';
          modalContainer.style.transition = 'opacity 0.2s ease-out';
          
          // Remove after animation
          setTimeout(() => {
            if (modalContainer.parentNode) {
              modalContainer.parentNode.removeChild(modalContainer);
            }
            (instance as any).modalContainer = null;
            (instance as any).modalIframe = null;
          }, 200);
        }
      },

      destroy: () => {
        instance.unmount();
        instance.closeModal();
        elementsRef.current.delete(elementId);
        triggerUpdate();
      }
    };

    // Helper functions
    function setupResizeHandling() {
      if (!iframe) return;
      
      // Convert string constraints to numbers for the unified resize system
      const convertConstraint = (value: number | string | undefined): number | undefined => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          if (value.includes('vh')) {
            return (parseInt(value) / 100) * window.innerHeight;
          }
          if (value.includes('vw')) {
            return (parseInt(value) / 100) * window.innerWidth;
          }
          return parseInt(value);
        }
        return undefined;
      };

      const numericConstraints = {
        elementId,
        minHeight: convertConstraint(instance.constraints.minHeight),
        maxHeight: convertConstraint(instance.constraints.maxHeight),
        minWidth: convertConstraint(instance.constraints.minWidth),
        maxWidth: convertConstraint(instance.constraints.maxWidth),
      };
      
      const { cleanup, manager } = setupUnifiedParentResize(
        iframe,
        ({ height, width }: { height: number; width: number }) => {
          const newDimensions = { height, width, elementId };
          instance.dimensions = { height, width };
          applyConstraints();
          onResize?.(newDimensions);
          triggerUpdate();
        },
        numericConstraints
      );
      
      cleanupResize = cleanup;
      resizeManager = manager;
    }

    function getIframeUrl(): string {
      const base = baseUrl || (environment === 'production' ? 'https://elements.smarterservices.com' : '/elements');
      const elementPath = type.startsWith('blockkit/') ? 'blockkit/renderer' : type;
      
      const jwt = createJWT({
        ...instance.config,
        _constraints: instance.constraints,
        elementId
      });

      return `${base}/${elementPath}?data=${encodeURIComponent(jwt)}`;
    }

    function applyConstraints() {
      if (!iframe || !wrapper) return;

      const constraintToCSS = (value: number | string): string => {
        if (typeof value === 'number') return `${value}px`;
        return value;
      };

      // Apply width constraints to wrapper
      if (autoResize) {
        wrapper.style.width = '100%';
      } else if (instance.constraints.maxWidth) {
        wrapper.style.width = constraintToCSS(instance.constraints.maxWidth);
        wrapper.style.maxWidth = constraintToCSS(instance.constraints.maxWidth);
      }

      if (instance.constraints.minWidth) {
        wrapper.style.minWidth = constraintToCSS(instance.constraints.minWidth);
      }

      // Always ensure wrapper doesn't exceed parent container
      wrapper.style.maxWidth = '100%';
      
      // Iframe always fills wrapper
      iframe.style.width = '100%';

      // Apply height constraints
      if (autoResize && instance.dimensions) {
        iframe.style.height = `${instance.dimensions.height}px`;
      } else {
        if (instance.constraints.maxHeight) {
          iframe.style.maxHeight = constraintToCSS(instance.constraints.maxHeight);
        }
        if (instance.constraints.minHeight) {
          iframe.style.minHeight = constraintToCSS(instance.constraints.minHeight);
        }
        
        if (instance.dimensions && instance.dimensions.height > 0) {
          let calculatedHeight = instance.dimensions.height;
          
          if (instance.constraints.maxHeight && typeof instance.constraints.maxHeight === 'number') {
            calculatedHeight = Math.min(calculatedHeight, instance.constraints.maxHeight);
          }
          if (instance.constraints.minHeight && typeof instance.constraints.minHeight === 'number') {
            calculatedHeight = Math.max(calculatedHeight, instance.constraints.minHeight);
          }
          
          iframe.style.height = `${calculatedHeight}px`;
        } else if (instance.constraints.minHeight) {
          iframe.style.height = constraintToCSS(instance.constraints.minHeight);
        } else {
          iframe.style.height = '300px';
        }
      }
    }

    function setupEventHandling() {
      const handleMessage = (event: MessageEvent) => {
        if (!iframe || event.source !== iframe.contentWindow) return;

        try {
          const { type: messageType, ...data } = event.data;

          if (data.elementId !== elementId) return;

          switch (messageType) {
            case 'resize':
              // Resize is handled by the unified resize system, ignore here
              break;

            case 'ready':
              instance.isReady = true;
              onReady?.();
              triggerUpdate();
              break;

            case 'error':
              const error = new Error(data.message || 'Element error');
              onError?.(error);
              break;

            default:
              onEvent?.(messageType, data);
              break;
          }
        } catch (error) {
          console.error('Error handling message from SmarterElement:', error);
          onError?.(error as Error);
        }
      };

      window.addEventListener('message', handleMessage);
      
      // Store cleanup function
      const originalDestroy = instance.destroy;
      instance.destroy = () => {
        window.removeEventListener('message', handleMessage);
        originalDestroy();
      };
    }

    // Store instance
    elementsRef.current.set(elementId, instance);
    triggerUpdate();

    return instance;
  }, [environment, baseUrl, defaultConstraints, triggerUpdate]);

  // Get all active elements
  const getElements = useCallback(() => {
    return Array.from(elementsRef.current.values());
  }, []);

  // Get element by ID
  const getElement = useCallback((elementId: string) => {
    return elementsRef.current.get(elementId) || null;
  }, []);

  // Destroy all elements
  const destroyAll = useCallback(() => {
    elementsRef.current.forEach(element => {
      element.destroy();
    });
    elementsRef.current.clear();
    triggerUpdate();
  }, [triggerUpdate]);

  return {
    create,
    getElements,
    getElement,
    destroyAll
  };
}

export default useSmarterElements;
